import { useEffect, useState } from "react";
import Loader from "../../../../../components/Loader";
import { CreateHandleUnsaveNavigation } from "../../../../../utilities/handleUnsaveNavigation";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { HandleUnsaveNavigationWithFun } from "../../../../../utilities/handleUnsaveNavigationWithFun";
import ShipperDetailTable from "../../../../../components/ShipperDetailTable/ShipperDetailTable";
import {
  generateUUID,
  isNullObject,
  notifySuccess,
  notifyWarning,
} from "../../../../../utilities/helper";
import TimeDurationField from "../../../../../components/TimeDuationField/TimeDurationField";

const ShipperManagement = ({
  setShowShippers,
  setRowData,
  drugData,
  setDrugData,
  actions,
  orderNumber,
}) => {
  const [load, setLoad] = useState(false);
  const [updatedDrugData, setUpdatedDrugData] = useState(null);
  const [isTimeBelowMinDisabled, setIsTimeBelowMinDisabled] = useState(true);
  const [isTimeAboveMaxDisabled, setIsTimeAboveMaxDisabled] = useState(true);
  const [timeBelowMinDuration, setTimeBelowMinDuration] = useState({
    day: "",
    hour: "",
    minute: "",
  });
  const [timeAboveMaxDuration, setTimeAboveMaxDuration] = useState({
    day: "",
    hour: "",
    minute: "",
  });

  const [isReset, setIsReset] = useState(false);

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const handleUnsaveNavigationwithFun = HandleUnsaveNavigationWithFun();
  const navigate = useNavigate();

  const createShipperSchema = yup.object().shape({
    tempMonitorId: yup.string(),
    minTemperature: yup.string().required("Minimum Temperature is required"),
    maxTemperature: yup.string().required("Maximum Temperature is required"),
  });

  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    setValue,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm({
    resolver: yupResolver(createShipperSchema),
    defaultValues: {
      tempMonitorId: "",
      minTemperature: "",
      maxTemperature: "",
      timeAboveMax: "",
      timeBelowMin: "",
    },
    mode: "onChange",
  });

  const values = watch();

  useEffect(() => {
    if (
      updatedDrugData &&
      Object.keys(updatedDrugData) &&
      values?.maxTemperature !== "" &&
      values?.minTemperature !== ""
    ) {
      let validKeys = new Set();
      Object.keys(updatedDrugData)?.forEach((key) => {
        if (updatedDrugData[key] !== null) {
          validKeys.add(key);
        }
      });
      let maxTemp = 999;
      let minTemp = -999;
      drugData.forEach((item) => {
        if (validKeys.has(item?.drugId)) {
          if (item?.triggerTemperatureMax) {
            maxTemp = item?.triggerTemperatureMax;
          }
          if (item?.triggerTemperatureMin) {
            minTemp = item?.triggerTemperatureMin;
          }
        }
      });
      if (values?.maxTemperature > maxTemp) {
        setIsTimeAboveMaxDisabled(false);
      }
      if (values?.maxTemperature < maxTemp) {
        setIsTimeAboveMaxDisabled(true);
      }
      if (values?.minTemperature < minTemp) {
        setIsTimeBelowMinDisabled(false);
      }
      if (values?.minTemperature > minTemp) {
        setIsTimeBelowMinDisabled(true);
      }
    } else {
      setIsTimeAboveMaxDisabled(true);
      setIsTimeBelowMinDisabled(true);
    }
  }, [updatedDrugData, values?.maxTemperature, values?.minTemperature]);

  const submitShipperForm = async (data) => {
    createRowData(true);
  };

  const handleShipperDetailChange = (updatedData) => {
    setUpdatedDrugData(updatedData);
  };

  const minTempRegister = register("minTemperature");
  const maxTempRegister = register("maxTemperature");

  const createRowData = async (goBack = false) => {
    // validate all fields
    const isValid = await trigger();

    if (!isValid) {
      // errors will already be set in formState.errors
      console.log("Validation failed");
      return;
    }

    if (isNullObject(updatedDrugData)) {
      notifyWarning("Please Select At Least One Kit to proceed.");
      return;
    }

    const values = getValues();
    const validIdsForDrugData = Object.entries(updatedDrugData)
      .filter(([_, value]) => value !== null && value !== undefined) // keep only non-null
      .map(([key]) => key);
    // Suppose validIdsForDrugData = ["id1", "id2", ...]
    const validIdsSet = new Set(validIdsForDrugData);

    const updatedData = drugData.filter((ele) => !validIdsSet.has(ele?.drugId));
    setDrugData(updatedData);
    const uuid = generateUUID();
    setValue("tempMonitorId", uuid);
    let obj = {
      id: Date.now().toString(36) + Math.random().toString(36).substring(2),
      tempMonitorId: uuid,
      maxTemperature: values?.maxTemperature,
      minTemperature: values?.minTemperature,
      timeBelowMin:
        timeBelowMinDuration?.day != "" &&
        timeBelowMinDuration?.hour !== "" &&
        timeBelowMinDuration?.minute !== ""
          ? `${timeBelowMinDuration?.day}d ${timeBelowMinDuration?.hour}h ${timeBelowMinDuration?.minute}m`
          : "00:00:00",
      assignedJson: updatedDrugData,
      timeAboveMax:
        timeAboveMaxDuration?.day != "" &&
        timeAboveMaxDuration?.hour !== "" &&
        timeAboveMaxDuration?.minute !== ""
          ? `${timeAboveMaxDuration?.day}d ${timeAboveMaxDuration?.hour}h ${timeAboveMaxDuration?.minute}m`
          : "00:00:00",
    };
    setRowData((prev) => [...prev, obj]);
    reset();
    setUpdatedDrugData(null);
    setIsTimeAboveMaxDisabled(true);
    setIsTimeBelowMinDisabled(true);
    setTimeAboveMaxDuration({
      day: "",
      hour: "",
      minute: "",
    });
    setTimeBelowMinDuration({
      day: "",
      hour: "",
      minute: "",
    });
    setIsReset(true);
    setTimeout(() => {
      setIsReset(false);
    }, 250);
    notifySuccess("Shipper Added Successfully !");
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    if (goBack === true) {
      setShowShippers(false);
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "drug-shipment-management",
                  navigate
                )
              }
            >
              Drug Order Management
            </button>{" "}
            |{" "}
            <button className="bread-crumbs-btn">
              Acknowledge Shipment Order{" "}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>

          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={4}>
              <p className="page-heading">
                Add Shipper for Order #{orderNumber}
              </p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{
              height: "auto",
            }}
            onSubmit={handleSubmit(submitShipperForm)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"center"}
              sx={{ width: "50%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <p className="labelStyles" style={{ flex: "0 0 30%" }}>
                    Temperature Monitor Id
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    disabled={true}
                    size="small"
                    type="text"
                    sx={{ width: "100%", background: "#f5f5f5" }}
                    name="tempMonitorId"
                    {...register("tempMonitorId")}
                  />
                </div>
                {errors.tempMonitorId && (
                  <div className="error-container">
                    {errors.tempMonitorId.message}
                  </div>
                )}
              </Grid2>

              <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <p className="labelStyles" style={{ flex: "0 0 30%" }}>
                    Min Temperature <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    {...minTempRegister}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Regex allows:
                      // - Optional leading minus
                      // - Digits before decimal
                      // - Optional single decimal with up to 2 digits after
                      const regex = /^-?\d*(\.\d{0,2})?$/;

                      if (!regex.test(value)) {
                        // 🚫 Invalid input → sanitize

                        // 1. Keep only digits, minus, and dot
                        value = value.replace(/[^0-9.-]/g, "");

                        // 2. Allow only one leading minus
                        value = value.replace(/(?!^)-/g, "");

                        // 3. Allow only one decimal
                        value = value.replace(/(\..*)\./g, "$1");

                        // 4. Restrict to max 2 digits after decimal
                        value = value.replace(/^(-?\d*\.\d{0,2}).*$/, "$1");
                      }

                      e.target.value = value;
                      minTempRegister.onChange(e);
                    }}
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </div>
                {errors.minTemperature && (
                  <div className="error-container">
                    {errors.minTemperature.message}
                  </div>
                )}
              </Grid2>

              <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <p className="labelStyles" style={{ flex: "0 0 30%" }}>
                    Max Temperature <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    {...maxTempRegister}
                    onChange={(e) => {
                      let value = e.target.value;

                      const regex = /^-?\d*(\.\d{0,2})?$/;

                      if (!regex.test(value)) {
                        value = value.replace(/[^0-9.-]/g, "");

                        value = value.replace(/(?!^)-/g, "");

                        value = value.replace(/(\..*)\./g, "$1");

                        value = value.replace(/^(-?\d*\.\d{0,2}).*$/, "$1");
                      }

                      e.target.value = value;
                      maxTempRegister.onChange(e);
                    }}
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </div>
                {errors.maxTemperature && (
                  <div className="error-container">
                    {errors.maxTemperature.message}
                  </div>
                )}
              </Grid2>

              <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <p className="labelStyles" style={{ flex: "0 0 30%" }}>
                    Time Below Min
                  </p>
                  <TimeDurationField
                    value={timeBelowMinDuration}
                    onChange={setTimeBelowMinDuration}
                    disable={isTimeBelowMinDisabled}
                    label="Set Duration"
                  />
                </div>
              </Grid2>

              <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <p className="labelStyles" style={{ flex: "0 0 30%" }}>
                    Time Above Max
                  </p>
                  <TimeDurationField
                    value={timeAboveMaxDuration}
                    onChange={setTimeAboveMaxDuration}
                    disable={isTimeAboveMaxDisabled}
                    label="Set Duration"
                  />
                </div>
              </Grid2>
            </Grid2>

            {drugData && drugData?.length > 0 ? (
              <div
                style={{
                  margin: "40px 0px",
                  maxWidth: "100%",
                  // overflowX: "auto",
                  // maxHeight: "650px",
                  // overflowY: "auto",
                }}
              >
                <ShipperDetailTable
                  actions={actions}
                  data={drugData}
                  onChange={handleShipperDetailChange}
                  isReset={isReset}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ color: "grey" }}>All Kits have been Used </h4>
              </div>
            )}

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigationwithFun(
                      e,
                      isDirty,
                      () => setShowShippers(false),
                      () => {}
                    );
                  }}
                >
                  Back
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button
                  type="button"
                  disabled={drugData.length === 0}
                  className={
                    drugData.length === 0 ? `disabled-btn` : `create-btn`
                  }
                  onClick={() => createRowData(false)}
                >
                  Save and Add Another
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button
                  type="submit"
                  className={
                    drugData.length === 0 ? `disabled-btn` : `create-btn`
                  }
                  disabled={drugData.length === 0}
                >
                  Save and Go Back
                </button>
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default ShipperManagement;
